/**
 * Registry of every store created via `defineState`, used only for SSR state transport.
 *
 * Quasar's generated server entry does `context.state = store.state` after `preFetch` but
 * *before* the render, while vue-server-renderer serialises `context.state` into
 * `window.__INITIAL_STATE__` *after* the render. `liveStates()` therefore returns live
 * references to the state objects rather than copies, so anything written during render
 * still reaches the client. This is exactly how Vuex behaves today.
 */

interface RegisteredStore {
  name: string
  get: () => object
  isCreated: () => boolean
  reset: () => void
}

const stores: RegisteredStore[] = []

export const _register = (store: RegisteredStore) => {
  if (stores.some(s => s.name === store.name)) {
    throw new Error(`[store] Duplicate store name "${store.name}".`)
  }

  stores.push(store)
}

/**
 * Live references to every store that has actually been created, keyed by name.
 * Stores that were never touched are omitted so the SSR payload stays small.
 */
export const liveStates = (): { [name: string]: object } => {
  const result: { [name: string]: object } = {}

  for (const store of stores) {
    if (store.isCreated()) {
      result[store.name] = store.get()
    }
  }

  return result
}

/**
 * Client-side counterpart, called from the store factory with `window.__INITIAL_STATE__`.
 * Assigns into the existing reactive state rather than replacing it, so references handed
 * out before hydration stay valid.
 */
export const hydrate = (snapshot: { [name: string]: object } | undefined) => {
  if (!snapshot) {
    return
  }

  for (const store of stores) {
    const incoming = snapshot[store.name]
    if (incoming !== void 0) {
      Object.assign(store.get(), incoming)
    }
  }
}

/**
 * Test seam, called from a global beforeEach.
 *
 * Every mount used to build a fresh Vuex store, so state could not leak from one test to the next.
 * These stores are module-scoped singletons that outlive a single test, so without this a value
 * written by one test is still there for the next — which is how a website page spec started
 * skipping its fetch because a previous test had already cached the stats.
 */
export const _resetAllStates = () => {
  for (const store of stores) {
    if (store.isCreated()) {
      store.reset()
    }
  }
}

/**
 * Test seam. Specs that call `defineState` at module scope would otherwise collide on
 * repeated registration across test files.
 */
export const _resetRegistry = () => {
  stores.length = 0
}

export const _registeredNames = () => stores.map(s => s.name)
